import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import { ReactComponent as HelpCircleIcon } from "images/help-circle.svg";
import { ReactComponent as InfoCircleOutlineIcon } from "images/info-circle-outline.svg";
import { cn } from "utils";

const HelpText = ({
  text,
  iconType,
  iconText,
  className,
  iconTextClassName,
  iconSize,
  children,
  toolTipClassName,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const usesPortalTooltip = iconType === "info" || iconType === "svg";

  const handleMouseEnter = () => {
    if (usesPortalTooltip) {
      updateTooltipPosition();
    }
    setIsVisible(true);
  };

  const updateTooltipPosition = () => {
    if (!triggerRef.current) {
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    setTooltipPosition({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  const renderIcon = () => {
    switch (iconType) {
      case "info": {
        return (
          <div
            className={cn(
              "inline-flex items-center justify-center cursor-help text-slate-700",
              iconTextClassName
            )}
          >
            <InfoCircleOutlineIcon
              style={{ width: iconSize, height: iconSize }}
              aria-hidden
            />
          </div>
        );
      }

      case "svg": {
        return (
          <div
            className={cn(
              "inline-flex items-center justify-center cursor-help",
              iconTextClassName
            )}
          >
            <HelpCircleIcon className="h-[21px] w-[21px]" aria-hidden />
          </div>
        );
      }

      case "text": {
        return (
          <p
            className={cn(
              "font-bold text-blue-500 cursor-help",
              iconTextClassName
            )}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {iconText}
          </p>
        );
      }

      default: {
        return <></>;
      }
    }
  };

  const portalTooltip =
    usesPortalTooltip &&
    isVisible &&
    createPortal(
      <div
        role="tooltip"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          borderRadius: "8px",
          boxShadow: "0px 4px 23.4px 0px #00000040",
        }}
        className={cn(
          "pointer-events-none fixed z-[9999] w-max max-w-xs -translate-x-1/2 border border-slate-200 bg-white p-6 text-base font-normal normal-case text-black transition-all font-rubik",
          {
            "opacity-100 translate-y-0": isVisible,
            "opacity-0 translate-y-1": !isVisible,
          },
          toolTipClassName
        )}
      >
        <span
          aria-hidden
          className="absolute -top-[7px] left-1/2 h-0 w-0 -translate-x-1/2 border-x-[7px] border-b-[7px] border-x-transparent border-b-slate-200"
        />
        <span
          aria-hidden
          className="absolute -top-[6px] left-1/2 h-0 w-0 -translate-x-1/2 border-x-[6px] border-b-[6px] border-x-transparent border-b-white"
        />
        {children ? children : <p className="font-rubik font-normal">{text}</p>}
      </div>,
      document.body
    );

  return (
    <div
      ref={triggerRef}
      className={`relative font-normal not-italic ${className}`}
      onMouseEnter={usesPortalTooltip ? handleMouseEnter : undefined}
      onMouseLeave={usesPortalTooltip ? handleMouseLeave : undefined}
    >
      {renderIcon()}

      {portalTooltip}

      {!usesPortalTooltip && (
        <div
          style={{ borderRadius: `20px`, cursor: `unset` }}
          className={cn(
            "pointer-events-none text-black absolute top-full left-0 z-10 mt-2 max-w-xs w-max -translate-x-1/2 bg-white p-4 font-rubik transition-all normal-case border-solid shadow-lg border-2 border-slate-200",
            {
              "translate-y-1": isVisible,
              "opacity-0 -translate-y-1": !isVisible,
            },
            toolTipClassName
          )}
        >
          {children ? (
            children
          ) : (
            <p className="font-rubik font-normal">{text}</p>
          )}
        </div>
      )}
    </div>
  );
};

HelpText.propTypes = {
  text: PropTypes.string,
  iconType: PropTypes.string,
  iconText: PropTypes.string,
  className: PropTypes.string,
  iconTextClassName: PropTypes.string,
  iconSize: PropTypes.number,
};

HelpText.defaultProps = {
  text: "",
  iconType: "svg",
  iconText: "",
  className: "",
  iconTextClassName: "",
  iconSize: 20,
};

export default HelpText;
